import { datadogRum } from '@datadog/browser-rum';
import { SparkProvider } from '@spark-web/core';
import { Heading } from '@spark-web/heading';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LDEvaluationDetail, LDProvider } from 'launchdarkly-react-client-sdk';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { useTranslation } from 'react-i18next';
import { Links, LoaderFunctionArgs, Meta, Outlet, Scripts, useLoaderData, useRouteError } from 'react-router';

import { Document } from './_document';
import ErrorImage from './assets/error.svg';
import { MaintenanceLayout } from './components/maintenance';
import { PartnerConfigProvider } from './context/PartnerConfigContext';
import i18next from './i18nnext.server';
import { LanguageProvider } from './provider/LanguageProvider';
import { ModalProvider } from './provider/ModalProvider';
import { fetchEnterprisePartnerByChannel } from './services/enterprisePartner';
import { MarketplaceChannel } from './types/marketplace';
import { getAllFlags } from './utils/launch-darkly';

type MaintenancePageFeatureFlagValue = {
  isEnabled: boolean;
  endDate?: string; // ISO string
};

// Type guard to check if error has status and statusText properties (for other error types)
function isErrorWithStatus(error: unknown): error is { status: number; statusText?: string; data: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as Record<string, unknown>).status === 'number'
  );
}

export async function loader({ request }: LoaderFunctionArgs) {
  const locale = await i18next.getLocale(request);
  const { searchParams } = new URL(request.url);
  const leadId = searchParams.get('leadId');
  const channelParam = searchParams.get('channel');
  // Default to 'brighte' when no channel param — mirrors the client-side nuqs withDefault('brighte')
  // so PartnerConfigContext is populated and price sort options are visible on the default Brighte flow.
  const channel = (channelParam ?? 'brighte') as MarketplaceChannel;

  // Partner lookup runs first so getAllFlags receives the resolved partner ID for accurate
  // server-side LD evaluation. Cache hit on warm Lambda makes this effectively free.
  const partnerResult = await fetchEnterprisePartnerByChannel(channel);
  const partner = partnerResult?.id ?? null;
  const activeChannel = partnerResult?.configuration?.channels?.find((c) => c.slug === channel) ?? null;

  const flags = await getAllFlags(leadId, { partner: partner ?? undefined });
  const maintenancePageFF = flags['maintenance-page-marketplace'] as MaintenancePageFeatureFlagValue;

  if (maintenancePageFF?.isEnabled) {
    throw new Response(JSON.stringify({ maintenance: maintenancePageFF }), {
      status: 503,
    });
  }

  return {
    locale,
    flags,
    leadId,
    partner,
    partnerConfig: partnerResult ?? null,
    activeChannel,
  };
}

export const handle = {
  // In the handle export, we can add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  // TIP: In most cases, you should set this to your defaultNS from your i18n config
  // or if you did not set one, set it to the i18next default namespace "translation"
  i18n: 'common',
};

export function Layout({ children }: { children: React.ReactNode }) {
  return <Document>{children}</Document>;
}

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  const { locale, flags, leadId, partner, partnerConfig, activeChannel } = useLoaderData<typeof loader>();
  const { i18n } = useTranslation();

  return (
    <PartnerConfigProvider config={partnerConfig} activeChannel={activeChannel}>
      <LDProvider
        clientSideID={import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID}
        context={{
          kind: 'user',
          key: leadId ? `lead|${leadId}` : 'anonymous',
          ...(!leadId && { anonymous: true }),
          partner: partner ?? undefined,
        }}
        options={{
          bootstrap: flags,
          inspectors: [
            {
              type: 'flag-used',
              name: 'dd-inspector',
              method: (key: string, detail: LDEvaluationDetail) => {
                datadogRum.addFeatureFlagEvaluation(key, detail.value);
              },
            },
          ],
        }}
      >
        <LanguageProvider locale={locale} dir={i18n.dir(locale)}>
          <NuqsAdapter>
            <QueryClientProvider client={queryClient}>
              <SparkProvider supportedThemes={['brighte', 'pantheon', 'bravo']}>
                <ModalProvider>
                  <Outlet />
                </ModalProvider>
              </SparkProvider>
            </QueryClientProvider>
          </NuqsAdapter>
        </LanguageProvider>
      </LDProvider>
    </PartnerConfigProvider>
  );
}

export default App;

export function ErrorBoundary() {
  const error = useRouteError();

  if (isErrorWithStatus(error) && error.status === 503) {
    const data = JSON.parse(error.data) as { maintenance: MaintenancePageFeatureFlagValue };

    return (
      <NuqsAdapter>
        <SparkProvider supportedThemes={['brighte', 'pantheon', 'bravo']}>
          <MaintenanceLayout endDate={data.maintenance.endDate} />
        </SparkProvider>
      </NuqsAdapter>
    );
  }

  if (import.meta.env.NODE_ENV !== 'production') {
    console.error('An error occurred in a route component', error);
  }

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="format-detection" content="telephone=no, date=no, email=no, address=no" />
        <meta name="robots" content="noindex, nofollow" />
        <Meta />
        <Links />
        <title>Error</title>
      </head>
      <body>
        <Stack gap="xxlarge" padding="xlarge" marginBottom="xlarge">
          <Stack align="center" gap="xlarge">
            <img src={ErrorImage} alt="error" />
          </Stack>
          <Heading level="1">Oops!</Heading>
          <Text>{"We're sorry something went wrong here. You can try the link again in some time."}</Text>
          <Text>
            If the error persists, please reach out to your vendor to request a fresh link. We apologise for the
            inconvenience.
          </Text>
          <Text>Brighte Team</Text>
        </Stack>
        <Scripts />
      </body>
    </html>
  );
}
